import React, { useState, useEffect } from "react";
import { Navigate, Outlet, useLocation, NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { Sun, LayoutDashboard, FolderOpen, Wallet, Users, Settings, CheckSquare } from "lucide-react";
import { can, canSeePayments } from "@/lib/permissions";
import { AnimatePresence, motion } from "framer-motion";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";

const MOBILE_NAV = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/app/dashboard", show: (u) => can(u, "page.dashboard") },
  { label: "Projects",  icon: FolderOpen,      path: "/app/projects",  show: (u) => can(u, "projects.view") },
  { label: "My Tasks",  icon: CheckSquare,     path: "/app/my-tasks",  show: (u) => can(u, "page.my_tasks") },
  { label: "Payments",  icon: Wallet,           path: "/app/payments",  show: (u) => canSeePayments(u) },
  { label: "Team",      icon: Users,            path: "/app/team",      show: (u) => can(u, "page.team") },
  { label: "Settings",  icon: Settings,         path: "/app/settings",  show: (u) => can(u, "page.settings") && !canSeePayments(u) },
];

const pageVariants = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -8 },
};

const pageTransition = {
  duration: 0.28,
  ease: [0.16, 1, 0.3, 1],
};

export default function AppLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // A tap on a drawer link navigates; the drawer must close with it.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (loading) {
    return (
      <div
        style={{ background: "#F5F8F5" }}
        className="min-h-screen flex items-center justify-center"
      >
        <div className="flex flex-col items-center gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center animate-glow-pulse"
            style={{ background: "#FFB703", boxShadow: "0 0 24px rgba(255,183,3,0.4)" }}
          >
            <Sun size={22} style={{ color: "#1B2B22" }} />
          </div>
          <div className="text-sm font-body" style={{ color: "#8AA496" }}>
            Loading SunBiz...
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <div
      className="flex h-screen overflow-hidden"
      style={{ background: "#F5F8F5" }}
    >
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 lg:hidden"
          style={{ background: "rgba(27,43,34,0.4)", backdropFilter: "blur(4px)" }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0 fixed lg:relative z-30 lg:z-auto h-full
          transition-transform duration-300
        `}
        style={{ flexShrink: 0 }}
      >
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <Header onMobileMenuToggle={() => setMobileOpen(!mobileOpen)} />
        <main
          className="flex-1 overflow-y-auto pb-16 lg:pb-0"
          style={{ background: "#F5F8F5" }}
          data-testid="main-content"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={pageTransition}
              style={{ minHeight: "100%" }}
            >
              <RouteErrorBoundary probe><Outlet /></RouteErrorBoundary>
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Mobile Bottom Navigation — only on small screens */}
        <nav
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around"
          style={{
            height: "60px",
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderTop: "1px solid rgba(46,100,68,0.1)",
          }}
        >
          {MOBILE_NAV.filter(i => i.show(user)).slice(0, 5).map(({ label, icon: Icon, path }) => (
            <NavLink
              key={path}
              to={path}
              style={({ isActive }) => ({
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "3px",
                padding: "6px 4px",
                flex: "1 1 0",
                minWidth: 0,
                textDecoration: "none",
                color: isActive ? "#2F7D52" : "#8AA496",
                transition: "color 0.15s ease",
                fontSize: "9px",
                fontWeight: "700",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontFamily: "Manrope, sans-serif",
              })}
            >
              {({ isActive }) => (
                <>
                  <Icon size={18} />
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
